import {
  RawConditionData,
  DifferentialRow,
  Condition,
  Section,
  TextOrListCard,
  CardDetails,
  DifferentialCard,
  SequelaeCard,
  AnyCard,
  TreatmentDetail,
  PharmacologicalCard,
} from './condition.models';

// 🔹 Generic grouping helper
function groupBy<T, K extends string | number>(
  arr: T[],
  keyGetter: (item: T) => K,
): Record<K, T[]> {
  return arr.reduce(
    (acc, item) => {
      const key = keyGetter(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<K, T[]>,
  );
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

function asString(x: unknown): string | null {
  return typeof x === 'string' && x.trim() ? x.trim() : null;
}

function asStringArray(x: unknown): string[] | null {
  if (!Array.isArray(x)) return null;
  const out = x.filter((v): v is string => typeof v === 'string' && !!v.trim());
  return out.length ? out.map((s) => s.trim()) : [];
}

export function transformCondition(raw: RawConditionData): Condition {
  const topUnknown: unknown =
    (raw as unknown as Record<string, unknown>)['condition'] ?? null;
  const detailsUnknown: unknown =
    (raw as unknown as Record<string, unknown>)['details'] ?? [];
  const taxonomyUnknown: unknown =
    (raw as unknown as Record<string, unknown>)['taxonomy'] ?? [];

  const top = isRecord(topUnknown) ? topUnknown : null;
  const details = Array.isArray(detailsUnknown)
    ? (detailsUnknown as any[])
    : [];
  const taxonomy = Array.isArray(taxonomyUnknown)
    ? (taxonomyUnknown as any[])
    : [];

  // Build sections only if we have details; otherwise leave empty
  let sections: Section[] = [];
  if (details.length) {
    const sectionsById = groupBy(details, (d) => d.section_id);

    sections = Object.values(sectionsById)
      .map((sectionRows): Section | null => {
        const firstRow = sectionRows[0];
        if (!firstRow) return null;

        const cardsById = groupBy(sectionRows, (r) => r.card_id);
        const mainCards: AnyCard[] = [];
        const sideCards: TextOrListCard[] = [];

        // --- Sequelae (special combined card) ---
        const shortRows = sectionRows
          .filter((r) => r.card_title === 'Short-term Sequelae')
          .sort((a, b) => a.detail_display_order - b.detail_display_order);

        const longRows = sectionRows
          .filter((r) => r.card_title === 'Long-term Sequelae')
          .sort((a, b) => a.detail_display_order - b.detail_display_order);

        if (shortRows.length || longRows.length) {
          const shortOrder = shortRows[0]?.card_display_order ?? Infinity;
          const longOrder = longRows[0]?.card_display_order ?? Infinity;
          const leftFirst = shortOrder <= longOrder;

          const sequelaeCard: (SequelaeCard & { order: number }) & {
            id?: string;
            display_order?: number;
          } = {
            kind: 'sequelae',
            short_term_title: leftFirst
              ? 'Short-term Sequelae'
              : 'Long-term Sequelae',
            long_term_title: leftFirst
              ? 'Long-term Sequelae'
              : 'Short-term Sequelae',
            short_term: shortRows.map((r) => r.formatted),
            long_term: longRows.map((r) => r.formatted),
            order: Math.min(shortOrder, longOrder),
          };
          mainCards.push(sequelaeCard as AnyCard);
        }

        // --- Involvement (special combined card) ---
        const involvementCategories = [
          'Organs',
          'Tissues',
          'Cells',
          'Chemical Mediators',
        ];
        const involvementRows = sectionRows.filter((r) =>
          involvementCategories.includes(r.card_title),
        );

        if (involvementRows.length > 0) {
          const groupsByCategory = groupBy(
            involvementRows,
            (r) => r.card_title,
          );
          const groups = Object.entries(groupsByCategory).map(
            ([category, rows]) => ({
              category,
              rows: rows
                .sort((a, b) => a.detail_display_order - b.detail_display_order)
                .map((r) => {
                  const boldMatch = r.formatted?.match(/\*\*(.+?)\*\*/);
                  return {
                    category,
                    title: boldMatch
                      ? boldMatch[1]
                      : (r.plain ?? '').split(' ')[0],
                    formatted: r.formatted,
                    plain: r.plain,
                    needs_review: r.needs_review,
                    needs_update: r.needs_update,
                  };
                }),
            }),
          );

          const involvementCard = {
            kind: 'involvement' as const,
            title: 'Involvement',
            groups,
            order: involvementRows[0].card_display_order,
          };
          mainCards.push(involvementCard as AnyCard);
        }

        // --- Regular cards (text/list/pharmacological) ---
        for (const cardRows of Object.values(cardsById)) {
          if (!cardRows?.length) continue;
          const firstCard = cardRows[0];
          if (!firstCard?.card_title) continue;

          // Skip ones we already combined above
          if (
            firstCard.card_title === 'Short-term Sequelae' ||
            firstCard.card_title === 'Long-term Sequelae' ||
            involvementCategories.includes(firstCard.card_title)
          ) {
            continue;
          }

          // Pharmacological
          if (firstCard.card_title === 'Pharmacological Treatments') {
            const pharmRows = [...cardRows].sort(
              (a, b) => a.detail_display_order - b.detail_display_order,
            );
            const treatments: TreatmentDetail[] = pharmRows
              .map((row) =>
                parsePharmacologicalFormatted(row.formatted || row.plain || ''),
              )
              .flat();

            const pharmCard: (PharmacologicalCard & { order: number }) & {
              id?: string;
              display_order?: number;
            } = {
              kind: 'pharmacological',
              title: 'Pharmacological Treatments',
              details: treatments,
              order: firstCard.card_display_order,
              id: firstCard.card_id,
              display_order: firstCard.card_display_order,
            };
            mainCards.push(pharmCard as AnyCard);
            continue;
          }

          // Default text/list card
          const cardDetails: CardDetails[] = cardRows
            .filter((cr) => cr.formatted || cr.plain)
            .map((cr) => ({
              id: cr.detail_id,
              ai_logs_id: cr.ai_logs_id,
              section_id: cr.section_id,
              card_id: cr.card_id,
              formatted: cr.formatted,
              plain: cr.plain,
              needs_review: cr.needs_review,
              needs_update: cr.needs_update,
              display_order: cr.detail_display_order,
            }));
          if (!cardDetails.length) continue;

          const card: (TextOrListCard & { order: number }) & {
            id?: string;
            display_order?: number;
          } = {
            id: firstCard.card_id,
            display_order: firstCard.card_display_order,
            kind: firstCard.card_type,
            title: firstCard.card_title,
            location: firstCard.card_location,
            details: cardDetails,
            order: firstCard.card_display_order,
          };

          if (firstCard.card_location === 'side') {
            sideCards.push(card as TextOrListCard);
          } else {
            mainCards.push(card as AnyCard);
          }
        }

        (mainCards as any[]).sort(
          (a, b) => (a.order ?? 999) - (b.order ?? 999),
        );
        if (!mainCards.length && !sideCards.length) return null;

        return {
          id: firstRow.section_id,
          title: firstRow.section_title,
          module_title: firstRow.module_title,
          order: firstRow.section_display_order,
          cards: mainCards,
          marginPanel: sideCards.length ? { cards: sideCards } : undefined,
        };
      })
      .filter((s): s is Section => s !== null)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }

  // ---------
  // SAFE path_segments
  // Prefer:
  //  1) top.path_segments (if present)
  //  2) first taxonomy row path_segments (if present)
  //  3) []
  // ---------
  const topPathSegments =
    top && 'path_segments' in top
      ? asStringArray((top as Record<string, unknown>)['path_segments'])
      : null;

  const firstTax = taxonomy.length ? taxonomy[0] : null;
  const taxPathSegments =
    firstTax && isRecord(firstTax) && 'path_segments' in firstTax
      ? asStringArray((firstTax as Record<string, unknown>)['path_segments'])
      : null;

  const path_segments = topPathSegments ?? taxPathSegments ?? [];

  // ---------
  // SAFE top fields (no crashes if top is null)
  // ---------
  const id = top ? (top['id'] as any) : undefined;
  const slug = top ? (asString(top['slug']) ?? undefined) : undefined;
  const title = top
    ? (asString(top['title']) ?? 'Error: Not Found')
    : 'Error: Not Found';
  const toc_title = top ? (asString(top['toc_title']) ?? undefined) : undefined;
  const seo_description = top
    ? (asString(top['seo_description']) ?? undefined)
    : undefined;

  return {
    id,
    slug,
    title,
    toc_title,
    seo_description,
    path_segments,
    sections,
  };
}

// 🔹 Transform differentials → DifferentialCard[]
export function transformDifferentials(
  rows: DifferentialRow[],
): DifferentialCard[] {
  const diffsById = groupBy(rows, (r) => r.differential_id);

  return Object.values(diffsById).map((rows) => {
    const diagnosisTitle =
      rows.find((r) => r.finding_type === 'diagnosis')?.condition_title ??
      'Diagnosis';
    const differentialTitle =
      rows.find((r) => r.finding_type === 'differential')?.differential_title ??
      'Differential';

    // Group by finding_order so each diagnosis & differential pair up
    const rowsByOrder = groupBy(rows, (r) => r.finding_order);

    const pairs = Object.values(rowsByOrder).map((pairRows) => {
      const diagnosis = pairRows.find((r) => r.finding_type === 'diagnosis');
      const differential = pairRows.find(
        (r) => r.finding_type === 'differential',
      );

      return {
        diagnosis_formatted: diagnosis?.finding_formatted ?? '',
        diagnosis_plain: diagnosis?.finding_plain ?? '',
        diagnosis_needs_review: diagnosis?.finding_needs_review ?? false,
        diagnosis_needs_update: diagnosis?.finding_needs_update ?? false,
        differential_formatted: differential?.finding_formatted ?? '',
        differential_plain: differential?.finding_plain ?? '',
        differential_needs_review: differential?.finding_needs_review ?? false,
        differential_needs_update: differential?.finding_needs_update ?? false,
      };
    });

    return {
      kind: 'differential',
      diagnosis_title: diagnosisTitle,
      differential_title: differentialTitle,
      pairs,
    };
  });
}

// 🔹 Build a full Condition (with details and special cards)
export function buildCondition(raw: RawConditionData): Condition {
  const baseCondition = transformCondition(raw);
  const differentialCards = transformDifferentials(raw.differentials);

  if (differentialCards.length > 0) {
    baseCondition.sections.push({
      title: raw.differentials[0]?.section_title ?? 'Differential Diagnoses',
      module_title: raw.details[0]?.module_title ?? '',
      order: raw.differentials[0]?.section_display_order ?? 999,
      cards: differentialCards,
    });
  }

  return baseCondition;
}

export function parsePharmacologicalFormatted(text: string): TreatmentDetail[] {
  if (!text) return [];

  const norm = text.replace(/\r\n?/g, '\n').trim();
  if (!norm) return [];

  // Split into treatments: |++|
  const rawItems = norm
    .split(/(?:^|\n)\s*\|\+\+\|\s*/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const results: TreatmentDetail[] = [];

  for (const item of rawItems) {
    // Split into sub-parts: |+|
    const parts = item
      .split(/(?:^|\n)\s*\|\+\|\s*/g)
      .map((s) => s.trim())
      .filter(Boolean);

    if (!parts.length) continue;

    let treatment: string | undefined;
    const mechParts: string[] = [];
    const seParts: string[] = [];
    let lastLabel: 'mechanism' | 'sideEffects' | 'treatment' | null = null;

    for (const part of parts) {
      if (
        !treatment &&
        !/^mechanism:/i.test(part) &&
        !/^side effects?:/i.test(part)
      ) {
        treatment = part;
        lastLabel = 'treatment';
        continue;
      }

      if (/^mechanism:/i.test(part)) {
        const val = part.replace(/^mechanism:\s*/i, '').trim();
        if (val) mechParts.push(val);
        lastLabel = 'mechanism';
        continue;
      }

      if (/^side effects?:/i.test(part)) {
        const val = part.replace(/^side effects?:\s*/i, '').trim();
        if (val) seParts.push(val);
        lastLabel = 'sideEffects';
        continue;
      }

      // unlabeled bullet goes to the most recent labeled section
      if (lastLabel === 'mechanism') {
        mechParts.push(part);
      } else if (lastLabel === 'sideEffects') {
        seParts.push(part);
      } else if (!treatment) {
        treatment = part;
        lastLabel = 'treatment';
      } else {
        mechParts.push(part); // default bucket
      }
    }

    results.push({
      treatment: treatment ?? 'none',
      mechanism: mechParts.length ? mechParts.join(' ; ') : undefined,
      sideEffects: seParts.length ? seParts.join(' ; ') : undefined,
    });
  }

  return results;
}
